import { IoMdDownload } from "react-icons/io";
import { MdRefresh, MdSearch } from "react-icons/md";
import ReactLoading from "react-loading";
import { Breadcrumb } from "../../Breadcrumb";

import { useTheme } from "styled-components";
import { OrderStatus } from "../../../enums/Orders";
import * as S from "./styles";
import { StatusTag } from "../../../pages/Report/pages/Orders/styles";
import { useTranslation } from "react-i18next";
import { useTranslateStatus } from "../../../pages/Report/pages/Orders/hooks/useTranslateStatus";

interface PageHeaderProps {
  buttonType?: "search";
  children?: React.ReactNode;
  customBreadcrumbString?: string;
  customButton?: boolean;
  description?: string;
  downloadFunction?: () => void;
  hasButtons?: boolean;
  hasBorder?: boolean;
  loading?: {
    type: "filters" | "data" | "csv";
    isLoading: boolean;
  };
  refreshFunction?: () => void;
  orderDetailsStatus?: OrderStatus | "loading";
  title: string;
}

export function PageHeader({
  children,
  buttonType,
  title,
  description,
  hasButtons,
  hasBorder = true,
  loading,
  customBreadcrumbString,
  customButton,
  downloadFunction,
  refreshFunction,
  orderDetailsStatus,
}: PageHeaderProps) {
  const theme = useTheme();

  const { t } = useTranslation();

  const { translateOrderStatus } = useTranslateStatus();

  return (
    <S.PageHeader noDescription={!description} hasBorder={hasBorder}>
      <Breadcrumb customBreadcrumbString={customBreadcrumbString} />

      {!orderDetailsStatus && (
        <S.Title noDescription={!description}>{title}</S.Title>
      )}

      {orderDetailsStatus && (
        <S.AlignTitle>
          <S.OrderDetailsTitle>{title}</S.OrderDetailsTitle>
          <StatusTag
            status={orderDetailsStatus}
            title={
              orderDetailsStatus !== "loading"
                ? translateOrderStatus(orderDetailsStatus)
                : ""
            }
          >
            {orderDetailsStatus === "loading" ? (
              <ReactLoading
                height="100%"
                width={18}
                type={"spin"}
                color={theme.colors["gray-800"]}
              />
            ) : (
              translateOrderStatus(orderDetailsStatus)
            )}
          </StatusTag>
        </S.AlignTitle>
      )}

      {(hasButtons || description || customButton) && (
        <S.DescriptionContainer isOrderDetails={!!orderDetailsStatus}>
          {description && <span>{description}</span>}
          {(hasButtons || customButton) && (
            <S.ButtonContainer>
              {hasButtons && (
                <>
                  {downloadFunction && (
                    <button
                      className="default-button"
                      disabled={loading?.type === "csv" && loading.isLoading}
                      onClick={downloadFunction}
                    >
                      {loading?.type === "csv" && loading?.isLoading ? (
                        <ReactLoading
                          height={16}
                          width={16}
                          type={"spin"}
                          color={theme.colors["gray-800"]}
                        />
                      ) : (
                        <IoMdDownload
                          size={20}
                          color={theme.colors["gray-800"]}
                        />
                      )}
                    </button>
                  )}
                  <button
                    className="default-button"
                    title={
                      buttonType === "search" ? t("search") : t("refreshPage")
                    }
                    disabled={loading?.type === "data" && loading.isLoading}
                    onClick={refreshFunction}
                  >
                    {loading?.type === "data" && loading.isLoading ? (
                      <ReactLoading
                        height={16}
                        width={16}
                        type={"spin"}
                        color={theme.colors["gray-800"]}
                      />
                    ) : buttonType === "search" ? (
                      <MdSearch size={20} color={theme.colors["gray-800"]} />
                    ) : (
                      <MdRefresh size={20} color={theme.colors["gray-800"]} />
                    )}
                  </button>
                </>
              )}
              {customButton && children}
            </S.ButtonContainer>
          )}
        </S.DescriptionContainer>
      )}
    </S.PageHeader>
  );
}
